import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { client, pub, errMsg } from "@/lib/api";

function Input({ field, value, onChange }) {
  const tid = `item-field-${field.key}`;
  if (field.type === "textarea") return <textarea data-testid={tid} className="field min-h-[100px]" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "lines") return <textarea data-testid={tid} className="field min-h-[100px]" value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} />;
  if (field.type === "bool") return <input type="checkbox" data-testid={tid} className="w-6 h-6 accent-[#2B5338]" checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
  if (field.type === "select") return <select data-testid={tid} className="field" value={value || field.default} onChange={(e) => onChange(e.target.value)}>{field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  return <input data-testid={tid} required={field.required} className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

function ItemDialog({ config, item, onClose, onSave }) {
  const [v, setV] = useState(() => item || Object.fromEntries(config.fields.filter((f) => f.default).map((f) => [f.key, f.default])));
  const submit = (e) => {
    e.preventDefault();
    const out = { ...v };
    if (out.options) out.options = out.options.map((s) => s.trim()).filter(Boolean);
    onSave(out);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="item-dialog">
        <DialogHeader><DialogTitle className="text-2xl">{item ? "Edit" : "Add"} {config.noun}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {config.fields.map((f) => (
            <label key={f.key} className={f.type === "bool" ? "flex items-center gap-3 font-semibold" : "block"}>
              <span className="block font-semibold mb-1">{f.label}</span>
              <Input field={f} value={v[f.key]} onChange={(x) => setV({ ...v, [f.key]: x })} />
            </label>
          ))}
          <button data-testid="item-save-button" className="btn-primary w-full">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ListEditor({ kind, config }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["overview"], queryFn: () => pub.get("/public/overview").then((r) => r.data) });
  const items = data?.[kind] || [];
  const [editing, setEditing] = useState(null);
  const api = client("admin");
  const refresh = () => qc.invalidateQueries({ queryKey: ["overview"] });

  const save = async (v) => {
    try {
      if (editing === "new") await api.post(`/admin/items/${kind}`, v);
      else await api.put(`/admin/items/${kind}/${editing.id}`, v);
      toast.success("Saved"); setEditing(null); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it[config.primary]}"?`)) return;
    try { await api.delete(`/admin/items/${kind}/${it.id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => {
    const a = items[i], b = items[i + d];
    if (!b) return;
    await Promise.all([api.put(`/admin/items/${kind}/${a.id}`, { order: i + d }), api.put(`/admin/items/${kind}/${b.id}`, { order: i })]);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid={`list-editor-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-3xl font-bold text-amber-950">{config.title}</h2>
        <button data-testid={`add-${kind}-button`} onClick={() => setEditing("new")} className="btn-primary"><Plus className="w-5 h-5" />Add {config.noun}</button>
      </div>
      {!items.length && <p className="text-lg text-stone-600">Nothing here yet.</p>}
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={it.id} data-testid={`${kind}-item-${it.id}`} className="card-warm !p-4 flex items-center gap-3">
            <div className="flex flex-col">
              <button aria-label="Move up" data-testid={`${kind}-up-${it.id}`} onClick={() => move(i, -1)} className="p-1 rounded hover:bg-stone-100 disabled:opacity-30" disabled={i === 0}><ArrowUp className="w-4 h-4" /></button>
              <button aria-label="Move down" data-testid={`${kind}-down-${it.id}`} onClick={() => move(i, 1)} className="p-1 rounded hover:bg-stone-100 disabled:opacity-30" disabled={i === items.length - 1}><ArrowDown className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 min-w-0"><p className="font-heading text-lg font-semibold">{it[config.primary]}</p><p className="text-stone-600 truncate">{config.sub(it)}</p></div>
            <button data-testid={`${kind}-edit-${it.id}`} onClick={() => setEditing(it)} className="btn-outline !min-h-[40px] !px-4"><Pencil className="w-4 h-4" />Edit</button>
            <button data-testid={`${kind}-delete-${it.id}`} onClick={() => remove(it)} className="btn-danger !min-h-[40px] !px-3" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      {editing && <ItemDialog config={config} item={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
